import React from 'react';

// Simple LCS line diff — snapshots are small (<10k chars) so O(n·m) is fine
export const diffLines = (aText, bText) => {
  const a = (aText || '').split('\n');
  const b = (bText || '').split('\n');
  const n = a.length, m = b.length;
  if (n * m > 500000) return null;
  const dp = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const out = [];
  let i = 0, j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) { out.push({ t: ' ', s: a[i] }); i++; j++; }
    else if (dp[i + 1][j] >= dp[i][j + 1]) { out.push({ t: '-', s: a[i] }); i++; }
    else { out.push({ t: '+', s: b[j] }); j++; }
  }
  while (i < n) out.push({ t: '-', s: a[i++] });
  while (j < m) out.push({ t: '+', s: b[j++] });
  return out;
};

const FIELD_LABELS = {
  system_prompt: 'Voice Prompt', chat_prompt: 'Chat Prompt', chat_prompt_enabled: 'Separate Chat Prompt', style_guardrails: 'Style Guardrails',
  response_guidelines: 'Response Guidelines', first_message: 'First Message',
  name: 'Agent Name', campaign_label: 'Campaign Label', chat_model: 'Chat Model',
  voice: 'Voice', voice_speed: 'Voice Speed', functions: 'Functions',
  response_wait_time: 'Response Delay', randomize_delay: 'Randomize Delay',
  callback_detection: 'Smart Callback Detection', textback_detection: 'Smart Textback Detection',
  callback_missed_workflow_id: 'Callback Missed Workflow', textback_workflow_id: 'Textback Workflow',
  knowledge_base_ids: 'Knowledge Bases', outbound_greeting: 'Outbound Greeting',
};
const LONG_TEXT_FIELDS = new Set(['system_prompt', 'chat_prompt', 'style_guardrails', 'response_guidelines', 'first_message', 'outbound_greeting']);
const SKIP_FIELDS = new Set(['updated_at', 'created_at']);

const fieldLabel = (k) => FIELD_LABELS[k] || k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const fmtVal = (v) => {
  if (v === null || v === undefined || v === '') return '(empty)';
  if (typeof v === 'boolean') return v ? 'On' : 'Off';
  if (Array.isArray(v)) return v.length ? v.join(', ') : '(none)';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

export const computeSnapshotChanges = (oldSnap, newSnap) => {
  const keys = new Set([...Object.keys(oldSnap || {}), ...Object.keys(newSnap || {})]);
  const changes = [];
  keys.forEach((k) => {
    if (SKIP_FIELDS.has(k)) return;
    const a = (oldSnap || {})[k];
    const b = (newSnap || {})[k];
    if (JSON.stringify(a ?? null) === JSON.stringify(b ?? null)) return;
    changes.push({ key: k, old: a, new: b, longText: LONG_TEXT_FIELDS.has(k) });
  });
  changes.sort((x, y) => (x.longText === y.longText ? x.key.localeCompare(y.key) : x.longText ? 1 : -1));
  return changes;
};

// Collapse runs of unchanged lines longer than `ctx`*2 into a separator
const collapseDiff = (lines, ctx = 3) => {
  if (!lines) return null;
  const out = [];
  let run = [];
  const flush = () => {
    if (run.length > ctx * 2 + 1) {
      out.push(...run.slice(0, ctx));
      out.push({ t: '…', s: `${run.length - ctx * 2} unchanged lines` });
      out.push(...run.slice(-ctx));
    } else {
      out.push(...run);
    }
    run = [];
  };
  lines.forEach((l) => {
    if (l.t === ' ') run.push(l);
    else { flush(); out.push(l); }
  });
  flush();
  return out;
};

export const VersionDiff = ({ changes, isDarkMode, textPrimary, textSecondary, cardBorder }) => {
  if (!changes || changes.length === 0) {
    return <p className={`text-sm ${textSecondary} text-center py-6`}>No differences between these versions.</p>;
  }
  return (
    <div className="space-y-3" data-testid="version-diff">
      {changes.map((c) => (
        <div key={c.key} className={`rounded-xl border ${cardBorder} overflow-hidden`}>
          <div className={`px-3 py-1.5 text-[11px] font-semibold ${textPrimary} ${isDarkMode ? 'bg-white/[0.04]' : 'bg-gray-50'} border-b ${cardBorder}`}>
            {fieldLabel(c.key)}
          </div>
          {c.longText ? (
            <div className="max-h-64 overflow-y-auto font-mono text-[11px] leading-5">
              {(collapseDiff(diffLines(fmtVal(c.old) === '(empty)' ? '' : String(c.old || ''), fmtVal(c.new) === '(empty)' ? '' : String(c.new || ''))) || []).map((l, i) => (
                l.t === '…' ? (
                  <div key={i} className={`px-3 py-0.5 text-center ${textSecondary} ${isDarkMode ? 'bg-white/[0.02]' : 'bg-gray-50'}`}>· · · {l.s} · · ·</div>
                ) : (
                  <div key={i} className={`px-3 whitespace-pre-wrap ${
                    l.t === '+' ? (isDarkMode ? 'bg-emerald-500/[0.12] text-emerald-300' : 'bg-emerald-50 text-emerald-700')
                    : l.t === '-' ? (isDarkMode ? 'bg-rose-500/[0.12] text-rose-300 line-through decoration-rose-400/40' : 'bg-rose-50 text-rose-600 line-through decoration-rose-300')
                    : textSecondary
                  }`}>
                    <span className="select-none mr-1.5 opacity-60">{l.t === ' ' ? ' ' : l.t}</span>{l.s || ' '}
                  </div>
                )
              ))}
            </div>
          ) : (
            <div className="px-3 py-2 flex items-center gap-2 text-xs flex-wrap">
              <span className={`px-2 py-0.5 rounded ${isDarkMode ? 'bg-rose-500/[0.12] text-rose-300' : 'bg-rose-50 text-rose-600'} line-through max-w-[45%] truncate`} title={fmtVal(c.old)}>{fmtVal(c.old)}</span>
              <span className={textSecondary}>→</span>
              <span className={`px-2 py-0.5 rounded ${isDarkMode ? 'bg-emerald-500/[0.12] text-emerald-300' : 'bg-emerald-50 text-emerald-700'} max-w-[45%] truncate`} title={fmtVal(c.new)}>{fmtVal(c.new)}</span>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default VersionDiff;
